import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { Router } from '@angular/router';
import { DataService } from '@core/services/data.service';
import { Language } from '@core/types/language.type';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { LogoService } from '@src/app/core/services/logo/logo.service';
import { AuthActions } from '@src/app/store/auth/auth.actions';
import { AuthSelectors } from '@src/app/store/auth/auth.selectors';
import { ScrollerOptions } from 'primeng/api';
import { SelectChangeEvent, SelectModule } from 'primeng/select';
import { Skeleton } from 'primeng/skeleton';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { ImportsModule } from 'src/app/imports';
import {
  AddEditLogoDialogComponent,
  AddEditLogoDialogMode,
} from '../../dialogs/add-edit-logo.dialog/add-edit-logo.dialog.component';

@Component({
  selector: 'app-header',
  imports: [
    CommonModule,
    ImportsModule,
    SelectModule,
    AddEditLogoDialogComponent,
    Skeleton,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent implements OnInit {
  dataService: DataService = inject(DataService);
  translate: TranslateService = inject(TranslateService);
  store: Store = inject(Store);
  router: Router = inject(Router);
  user = this.store.selectSignal(AuthSelectors.selectAuthUser);
  loadingUserInfo = this.store.selectSignal(AuthSelectors.selectLoadingUser);
  companiesParams = this.store.selectSignal(
    AuthSelectors.selectCompaniesParams,
  );
  companiesPagination = this.store.selectSignal(
    AuthSelectors.selectCompaniesPagination,
  );
  loadingCompanies = this.store.selectSignal(
    AuthSelectors.selectLoadingCompanies,
  );
  loadingKam = this.store.selectSignal(AuthSelectors.selectLoadingKam);

  addEditLogoDialogVisible = signal<boolean>(false);
  addEditLogoDialogMode = signal<AddEditLogoDialogMode>(
    AddEditLogoDialogMode.ADD,
  );
  companies = this.store.selectSignal(AuthSelectors.selectCompanies);
  loadingUser = this.store.selectSignal(AuthSelectors.selectLoadingUser);
  changingCompany = this.store.selectSignal(
    AuthSelectors.selectChangingCompany,
  );
  virtualScrollOptions: ScrollerOptions = {
    lazy: true,
    onScroll: this.onLazyLoad.bind(this),
  };

  // Subject para debounce del filtro de empresas
  private filterCompaniesSubject = new Subject<string>();

  selectedCompany = this.store.selectSignal(
    AuthSelectors.selectSelectedCompany,
  );
  isLoadingSelect = computed(
    () =>
      this.loadingUser() ||
      this.loadingKam() ||
      this.loadingCompanies() ||
      this.changingCompany(),
  );
  existingFile = computed(() => {
    const company = this.selectedCompany();
    return this.addEditLogoDialogMode() === AddEditLogoDialogMode.EDIT &&
      company?.logoUrl
      ? {
          objectURL: company.logoUrl,
          name: company.TERCE_DS || '',
          size: 0,
        }
      : null;
  });
  languages: Language[];
  selectedLanguage: Language;

  constructor(private logoService: LogoService) {}

  ngOnInit() {
    this.languages = this.dataService.getLanguages();
    this.selectedLanguage = this.dataService.getLanguages()[0];
    this.setupFilterCompaniesDebounce();
  }

  /**
   * Configura el debounce para el filtro de empresas
   */
  private setupFilterCompaniesDebounce(): void {
    this.filterCompaniesSubject
      .pipe(debounceTime(250), distinctUntilChanged())
      .subscribe((filterValue) => {
        this.store.dispatch(AuthActions.clearCompanies());
        this.store.dispatch(
          AuthActions.updateCompaniesParams({
            params: { name: filterValue, page: 1 },
          }),
        );
        this.store.dispatch(AuthActions.loadCompanies());
      });
  }

  onLazyLoad(event: any) {
    const target = event.originalEvent.target as HTMLElement;
    const currentParams = this.companiesParams();
    const currentCompanies = this.companies();
    const companiesPagination = this.companiesPagination();

    // Obtener información del scroll del evento
    const scrollTop = target.scrollTop || 0;
    const scrollHeight = target.scrollHeight || 0;
    const clientHeight = target.clientHeight || 0;

    // Calcular el porcentaje de scroll: (scrollTop + clientHeight) / scrollHeight
    const scrollPosition = scrollTop + clientHeight;
    const scrollPercentage =
      scrollHeight > 0 ? scrollPosition / scrollHeight : 0;

    // Cargar más datos cuando se alcanza el 80% del scroll
    if (
      scrollPercentage >= 0.8 &&
      !this.loadingCompanies() &&
      companiesPagination &&
      (companiesPagination.total ?? 0) > currentCompanies.length
    ) {
      this.store.dispatch(
        AuthActions.updateCompaniesParams({
          params: { ...currentParams, page: (currentParams?.page ?? 0) + 1 },
        }),
      );
      this.store.dispatch(AuthActions.loadCompanies());
    }
  }

  filterCompanies(event: { originalEvent: Event; filter: string }) {
    this.filterCompaniesSubject.next(event.filter);
  }

  changeLanguage(language: SelectChangeEvent) {
    this.selectedLanguage = language.value;
    this.translate.use(language.value.code);
  }

  changeCompany(event: SelectChangeEvent) {
    const selectedClienteNm = event.value;
    const company = this.companies().find(
      (c) => c.CLIEN_NM === selectedClienteNm,
    );
    if (company) {
      this.store.dispatch(AuthActions.setSelectedCompany({ company }));
    }
  }

  addLogo() {
    // Smart mode detection: check if company already has a logo
    const companyId = this.selectedCompany()?.CLIEN_NM;
    if (companyId) {
      this.logoService.findExistingLogo(companyId).subscribe((existingLogo) => {
        if (existingLogo) {
          this.addEditLogoDialogMode.set(AddEditLogoDialogMode.EDIT);
        } else {
          this.addEditLogoDialogMode.set(AddEditLogoDialogMode.ADD);
        }
        this.addEditLogoDialogVisible.set(true);
      });
    } else {
      // Fallback to ADD mode if no company selected
      this.addEditLogoDialogMode.set(AddEditLogoDialogMode.ADD);
      this.addEditLogoDialogVisible.set(true);
    }
  }

  editLogo() {
    this.addEditLogoDialogMode.set(AddEditLogoDialogMode.EDIT);
    this.addEditLogoDialogVisible.set(true);
  }
}
